import { Mail, MessageSquare } from 'lucide-react';
import type { GuestCommunicationChannel } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { Occasion } from './journeyModel';

/** Auswahl eines Anlasses: Zeitpunkt, Titel, Kanäle und Inhaltsstand — der Schalter steht darunter. */
export function OccasionButton({
  occasion,
  enabled,
  selected,
  onSelect,
}: {
  occasion: Occasion;
  enabled: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        '@2xl:min-h-[98px] @2xl:h-full @2xl:py-2.5 @2xl:gap-1.5 focus-visible:outline-sage relative flex w-full min-w-0 flex-col items-start gap-1 rounded-xl border px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        selected ? 'border-navy bg-navy' : 'border-line/70 hover:border-ink-faint bg-white',
      )}
    >
      {/* Breit steht der Zeitpunkt über der Zeitleiste; vorgelesen gehört er zum Anlass. */}
      <span
        className={cn(
          '@2xl:sr-only font-mono text-[10.5px] uppercase tracking-[0.06em]',
          selected ? 'text-paper/70' : 'text-ink-soft',
        )}
      >
        {occasion.when}
      </span>
      <span
        className={cn(
          'font-serif text-[16.5px] leading-tight',
          selected ? 'text-paper' : enabled ? 'text-ink' : 'text-ink-soft',
        )}
      >
        {occasion.title}
      </span>
      <span
        className={cn(
          'flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11.5px]',
          selected ? 'text-paper/80' : 'text-ink-soft',
        )}
      >
        <ChannelMarks channels={occasion.channels} />
        {occasion.required && <span>Pflicht</span>}
        {occasion.issues > 0 && (
          <span className={cn('font-semibold', selected ? 'text-sealwax-on-dark' : 'text-sealwax')}>
            {occasion.issues === 1 ? '1 Feld prüfen' : `${occasion.issues} Felder prüfen`}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn(
              'size-2 rounded-full',
              !occasion.customized && 'ring-[1.5px] ring-inset ring-current',
              occasion.customized && (selected ? 'bg-paper/70' : 'bg-postmark'),
            )}
          />
          {occasion.customized ? 'Angepasst' : 'Standard'}
        </span>
      </span>
      {occasion.unsaved && (
        <>
          <span aria-hidden className="bg-amber absolute right-2.5 top-2.5 size-2 rounded-full" />
          <span className="sr-only">ungespeicherte Änderung</span>
        </>
      )}
    </button>
  );
}

function ChannelMarks({ channels }: { channels: GuestCommunicationChannel[] }) {
  const ota = channels.includes('Ota');
  return (
    <span className="inline-flex gap-1.5">
      {channels.includes('Email') && <Mail size={14} aria-hidden />}
      {ota && <MessageSquare size={14} aria-hidden />}
      <span className="sr-only">{ota ? 'E-Mail und OTA-Nachricht' : 'nur E-Mail'}</span>
    </span>
  );
}

/** Punkt auf der Zeitleiste: Pflicht, versendet oder ruhend. */
export function OccasionNode({
  required,
  enabled,
  className,
}: {
  required: boolean;
  enabled: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative z-[1] block size-3.5 shrink-0 rounded-full',
        required
          ? 'bg-navy ring-card ring-[3px]'
          : enabled
            ? 'bg-sage ring-card ring-[3px]'
            : 'bg-card ring-ink-faint ring-2 ring-inset',
        className,
      )}
    />
  );
}
